import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Removes an entire pack (every cart line that shares a `_pack` id, gifts included)
 * in a single cart/update call, then dispatches a CartUpdateEvent so all
 * <cart-items-component> instances re-render (morph).
 *
 * Markup:
 *   <cart-pack-remove data-keys="key1,key2,key3">
 *     <button type="button">Quitar</button>
 *   </cart-pack-remove>
 */
class CartPackRemove extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    // The remove button lives inside the pack <summary>; keep the click from toggling it.
    event.preventDefault();
    event.stopPropagation();

    const keys = (this.dataset.keys || '').split(',').map((k) => k.trim()).filter(Boolean);
    if (!keys.length) return;

    const owner = this.closest('cart-items-component');
    const sectionId = owner?.dataset.sectionId;
    if (!sectionId) return;

    const sections = new Set([sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sections.add(el.dataset.sectionId);
    });

    document.querySelectorAll('cart-items-component').forEach((el) => el.classList.add('cart-items-disabled'));

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sections).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((text) => {
        const parsed = JSON.parse(text);
        if (parsed.errors) return;
        this.dispatchEvent(
          new CartUpdateEvent(parsed, sectionId, {
            source: 'cart-pack-remove',
            sections: parsed.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-pack-remove]', error))
      .finally(() => {
        document.querySelectorAll('cart-items-component').forEach((el) => el.classList.remove('cart-items-disabled'));
      });
  };
}

if (!customElements.get('cart-pack-remove')) {
  customElements.define('cart-pack-remove', CartPackRemove);
}

/**
 * Toggles a pack card's expandable component list open/closed by flipping the
 * `is-open` class on the closest `.eo-line--pack`. Decoupled from <details> so the
 * toggle bar can sit next to the title while the list spans the full card width.
 */
class EoPackToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    this.closest('.eo-line--pack')?.classList.toggle('is-open');
  };
}

if (!customElements.get('eo-pack-toggle')) {
  customElements.define('eo-pack-toggle', EoPackToggle);
}
